import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { observer } from "mobx-react-lite";
import { HomeContainer } from "@client/pages/Home/Home.styled";
import { GlobalLoader } from "@common/components/GlobalLoader/GlobalLoader";
import { ConfirmActionModal } from "@common/components/Modal/ConfirmActionModal/ConfirmActionModal";
import { useStores } from "@client/store";
import {
  Block,
  Flex,
  Spacer,
  TabSection,
  Typography,
  useTheme,
} from "@ugc-ui/lego";
import {
  CreateCampaignsControl,
  CreateCampaignTabs,
} from "@client/pages/CreateCampaign/CreateCampaign.styled";
import { ShowCampaignWrapper } from "@client/pages/ShowCampaign/ShowCampaign.styled";
import { useIsMobile } from "@common/hooks/isMobile";
import { useHorizontalScroll } from "@common/hooks/useHorizontalScroll";
import { EditCampaignTabView } from "@client/pages/EditCampaign/Tabs/EditCampaignTabView";
import { EditCampaignTabStatistic } from "@client/pages/EditCampaign/Tabs/EditCampaignTabStatistic";
import { ConfirmActionModalType } from "@common/components/Modal/ConfirmActionModal/ConfirmActionModal.utils";
import { VideoUploadErrorModal } from "@common/components/ModalVideoUploadError/VideoUploadErrorModal";
import { NoticeNotAvailableAction } from "@common/components/Notice/NoticeNotAvailableAction/NoticeNotAvailableAction";
import { Header } from "@client/pages/Home/Header/Header";
import { InitialDialog } from "@client/pages/Home/InitialDialog/InitialDialog";
import { ModalVideoPlayer } from "@client/components/ModalVideoPlayer/ModalVideoPlayer";
import { DrawerUploadVideoMenu } from "@client/components/DrawerUploadVideoMenu/DrawerUploadVideoMenu";
import { ButtonBack } from "@common/components/ButtonBack/ButtonBack";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage } from "@common/components/Interests/ErrorMessage";
import { ViewTouchSkeletonPage } from "@common/components/TabView/ViewTouchSkeletonPage";
import { ViewDesktopSkeletonPage } from "@common/components/TabView/ViewDesktopSkeletonPage";
import { getCompanyPage } from "@common/consts/Path";

export enum EditCampaignGroup {
  VIEW = "view",
  STATISTIC = "statistic",
}

export const EditCampaignPage = observer(() => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { exitModalStore } = useStores();

  const {
    client: {
      notAvailableActionNotice,
      uploadAreaStore: { loadingProgress, detach: uploadAreaDetach },
      showCampaignDialogStore,
    },
    uploadAreaErrorModal,
  } = useStores();
  const { attach, campaignData, errorMessage, detach } =
    showCampaignDialogStore;

  const { uuid } = useParams();

  useEffect(() => {
    attach(uuid);
    return () => {
      detach();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  const ref = useHorizontalScroll<HTMLDivElement>();
  const [tab, setTab] = useState(EditCampaignGroup.VIEW);
  const handleClickOnCampaignTab = (newTab) => setTab(newTab);

  if (errorMessage) {
    return (
      <ErrorMessage
        onClick={() => {
          navigate(0);
        }}
      />
    );
  }

  const skeletonPage = isMobile ? (
    <ViewTouchSkeletonPage />
  ) : (
    <ViewDesktopSkeletonPage />
  );

  return (
    <>
      <Helmet>
        <title>Рекламный кабинет - редактирование кампании</title>
      </Helmet>
      {!isMobile && <Header />}

      <Block dataCy="страница редактирования рекламной кампании">
        <HomeContainer>
          {campaignData ? (
            <ShowCampaignWrapper>
              <div>
                <ButtonBack
                  title="Редактирование рекламной кампании"
                  type={ConfirmActionModalType.CANCEL_EDIT}
                  exitModalStore={exitModalStore}
                  navigateTo={getCompanyPage(campaignData.uuid)}
                />

                <CreateCampaignsControl theme={theme}>
                  <CreateCampaignTabs
                    ref={ref}
                    dataCy="контейнер-табов"
                    value={tab}
                    onChange={handleClickOnCampaignTab}
                    padding="0"
                    underlineHeight={1}
                  >
                    <TabSection label="Обзор" value={EditCampaignGroup.VIEW} />
                    <TabSection
                      label="Статистика"
                      value={EditCampaignGroup.STATISTIC}
                    />
                  </CreateCampaignTabs>
                  {!isMobile && (
                    <Typography type="subtitle2" color={theme.gray.a40}>
                      Черновик
                    </Typography>
                  )}
                </CreateCampaignsControl>
              </div>
              <Spacer size={28} vertical />
              {tab === EditCampaignGroup.VIEW ? (
                <EditCampaignTabView />
              ) : (
                <EditCampaignTabStatistic
                  campaign={showCampaignDialogStore.campaignData}
                />
              )}
              <Flex />
            </ShowCampaignWrapper>
          ) : (
            skeletonPage
          )}
        </HomeContainer>
      </Block>
      <GlobalLoader />

      <VideoUploadErrorModal uploadAreaErrorModalStore={uploadAreaErrorModal} />
      <ConfirmActionModal exitModalStore={exitModalStore} />
      <NoticeNotAvailableAction
        isOpen={notAvailableActionNotice.isOpen}
        // eslint-disable-next-line react/jsx-handler-names
        onCloseDialog={notAvailableActionNotice.closeDialog}
      />
      <InitialDialog />
      <NoticeNotAvailableAction
        isOpen={notAvailableActionNotice.isOpen}
        // eslint-disable-next-line react/jsx-handler-names
        onCloseDialog={notAvailableActionNotice.closeDialog}
      />
      <ConfirmActionModal exitModalStore={exitModalStore} />
      <ModalVideoPlayer />
      <DrawerUploadVideoMenu uploadAreaDetach={uploadAreaDetach} />
      <GlobalLoader progress={loadingProgress} />
    </>
  );
});
